import { canUseDOM } from 'exenv';
import Highcharts from 'highcharts';
import i18next, { t } from 'i18next';
import _ from 'lodash';
import moment from 'moment';
import 'moment/min/locales';
import Numeral from 'numeral';
import qs from 'qs';
import { useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import {
  formatPhaseDisplay,
  getPhaseProgress,
  getPhaseSegments
} from '../../../../../../shared/common/bossPhases';
import { roundScore } from '../../../../../../shared/common/utils';
import Chart from '../../../../../../shared/frontend/components/chart';
import { FormControlSlider } from '../../../../../../shared/frontend/components/form/FormControlSlider';
import { Stack } from '../../../../../../shared/frontend/components/stack/Stack';
import { Typography } from '../../../../../../shared/frontend/components/typography';
import {
  formatMsNoSeconds,
  getDefaultTimezone,
  plural,
  relativeDate
} from '../../../../../../shared/frontend/libs/utils';
import { RouteComponentProps } from '../../../../shared/types/common';
import { getGuildLogo } from '../../../../shared/utils';
import { withSiteMeta } from '../../../../../../shared/frontend/HOCS/withSiteMeta';
import { ComplexTooltip } from '../../../../../../shared/frontend/components/presentational/tooltip';
import { buildComparisonSeriesStyles } from '../../shared/utils/chartSeriesStyles';
import { apiServiceAsync } from '../../services/apiService';
import { useWidgetVersion } from '../hooks/useWidgetVersion';
import {
  buildPercentPhaseAxisConfig,
  buildHealthOverAttemptBackgroundColor,
  buildHealthOverAttemptSubtitle,
  filterSeriesDataByPhaseSegmentIndexes,
  getTranslatedHealthOverAttemptLabel,
  parseHealthOverAttemptConfig,
  THealthOverAttemptParsedConfig
} from './healthOverAttemptPresenter';

/* tslint:disable:max-line-length */

/* tslint:disable:no-var-requires */
if (canUseDOM) {
  require('highcharts/modules/annotations')(Highcharts);
  // require('highcharts/modules/debugger')(Highcharts);
}
/* tslint:enable:no-var-requires */

export type TStreamWidgetHealthOverAttemptProps = RouteComponentProps<any, any> & {
  onPhasesChange?: (phases: string | null) => void;
};

const ABSOLUTE_DATE_FORMAT = 'MMM D HH:mm';

const TEST_PAYLOAD = getTestPayload();

function generatePullTooltip(widgetType: TWidgetType, difficulty: string, bossSlug: string) {
  /* tslint:disable:only-arrow-functions */
  return function gen() {
    const phaseProgress = getPhaseProgress(difficulty, bossSlug, {
      overallPercent: this.y / 100,
      elapsedMs: this.options?.custom?.durationMs
    });
    const progressString = phaseProgress
      ? formatPhaseDisplay(phaseProgress, (bossPercent) => `${roundScore(bossPercent * 100, 1)}%`)
      : t('widgets:boss-health-progression.widget.widget-tooltip.encounter-with-percentage', {
          percentage: Numeral(this.y).format('0.0')
        });

    return `
<div class="rio-highcharts-tooltip tooltip--mythic-plus" style="border: 1px solid ${this.color}">
  <div>${this.series.name}</div>
  ${widgetType.tooltipFormatter(this)}
  <div>${progressString}</div>
</div>`;
  };
  /* tslint:enable:only-arrow-functions */
}

const GUILD_IMAGE_SIZE = 32;

function drawTicks(config: THealthOverAttemptParsedConfig, guildData: any) {
  const { translateX: tx, translateY: ty } = this.legend.group;

  this.series.forEach((series: any) => {
    let img = series.img;

    if (!img) {
      const guildId = series.options?.custom?.guildId || 0;

      const gd = guildData.guilds.filter((g: any) => g.id === guildId)[0];

      const shareRaidUntil = gd.guildPrivacy?.shareRaidUntil;
      const isOnDarkMode = shareRaidUntil ? moment.utc(shareRaidUntil).isBefore(moment.utc()) : false;

      const guildImageLogo = gd?.logo || getGuildLogo(guildId);

      if (!guildImageLogo) {
        return;
      }

      series.img = img = this.renderer
        .image(guildImageLogo, -9999, 0, GUILD_IMAGE_SIZE, GUILD_IMAGE_SIZE)
        .attr({
          zIndex: 10,
          opacity: isOnDarkMode ? 0.6 : 1
        })
        .add();
    }

    if (!series.legendSymbol) {
      return;
    }

    img.attr({
      x: series.legendGroup.translateX + tx - 40,
      y: series.legendGroup.translateY + ty + 5
    });
  });

  if (!config.hideLogo && this.chartWidth > 850) {
    const HEIGHT_PERCENT_FOR_CHART = 0.05;
    const imageHeight = Math.max(this.chartHeight, 850);
    this.renderer
      .image(
        'https://cdn.raiderio.net/images/brand/Logo_2ColorWhite.svg',
        imageHeight * 0.015,
        imageHeight * 0.015,
        (91 / 18) * (imageHeight * HEIGHT_PERCENT_FOR_CHART),
        imageHeight * HEIGHT_PERCENT_FOR_CHART
      )
      .add();
  }
}

type TWidgetType = {
  slug: string;
  subtitleKey: string;
  value: (attempt: any, previousValue: number | null) => number;
  xAxisType: string;
  formatter: (chart: any) => string;
  tooltipFormatter: (point: any) => string;
  plotLinesFilter: (line: any) => boolean;
  credits?: string | (() => string);
  addCustomData?: (guildSeriesData: any, guildBestSeriesData: any) => void;
};

const widgetTypes: TWidgetType[] = [
  {
    slug: 'attempt',
    subtitleKey: 'widgets:boss-health-progression.widget.progress-by-pull-count',
    value: (attempt) => attempt.attemptNumber,
    xAxisType: 'category',
    tooltipFormatter: (point) =>
      `${t('widgets:boss-health-progression.widget.widget-tooltip.pull-with-count', {
        count: point.x
      })}
      `,
    formatter: (chart) => {
      return chart.value;
    },
    plotLinesFilter: (d) => d.day !== 1
  },
  {
    slug: 'absoluteTime',
    subtitleKey: 'widgets:boss-health-progression.widget.progress-by-pull-date',
    value: (attempt) => moment.utc(attempt.details.pull_started_at).unix(),
    xAxisType: 'datetime',
    tooltipFormatter: (point) =>
      // TODO: Replace the calls from i18next.language to get the currentLocale from the getter in utils. Will also need custom logic for certain localizations. E.G: Moment stores zh as zh-cn.
      `${moment.unix(point.x).locale(i18next.language).format(ABSOLUTE_DATE_FORMAT)}: ${Numeral(point.y).format(
        '0.0'
      )}%`,
    formatter: (chart) => {
      return `${moment.unix(chart.value).tz(getDefaultTimezone()).locale(i18next.language).format(ABSOLUTE_DATE_FORMAT)}`;
    },
    credits: () =>
      `${t('widgets:boss-health-progression.widget.times-in-timezone', { timezone: getDefaultTimezone() })} - `,
    plotLinesFilter: () => true,
    // Add "null" data when break
    addCustomData: (guildSeriesData, guildBestSeriesData) => {
      let previousValue = null;
      const newData = [];
      for (const xData of guildSeriesData) {
        const startedAt = moment.unix(xData.x);
        if (previousValue === null) {
          previousValue = startedAt;
        } else {
          if (startedAt.diff(previousValue, 'hours') >= 5) {
            newData.push({
              x: previousValue.add(2, 'hours').unix(),
              y: null,
              custom: previousValue.toISOString()
            });
          }
        }
        previousValue = startedAt;
      }

      guildSeriesData.push(...newData);
      guildBestSeriesData.push(...newData);

      guildSeriesData.sort((a: any, b: any) => a.x - b.x);
      guildBestSeriesData.sort((a: any, b: any) => a.x - b.x);
    }
  },
  {
    slug: 'relativeTime',
    subtitleKey: 'widgets:boss-health-progression.widget.progress-by-time-spent',
    value: (attempt, previousValue) => (previousValue || 0) + attempt.details.duration_ms,
    xAxisType: 'category',
    tooltipFormatter: (point) => `${formatMsNoSeconds(point.x)}: ${Numeral(point.y).format('0.0')}%`,
    formatter: (chart: any) => formatMsNoSeconds(chart.value),
    plotLinesFilter: (d) => d.day !== 1
  }
];

/**
 * WidgetHealthOverAttempt
 *
 * Shows how many pulls were taken per boss.
 */
let timeoutHandle: any = null;

function WidgetHealthOverAttempt(props: TStreamWidgetHealthOverAttemptProps) {
  if (!canUseDOM) {
    // do not attempt to render on server
    return null;
  }

  const [data, setData] = useState(null as any);
  useWidgetVersion(data?.widgetVersion);
  const graphOption = useRef(new Map<number, boolean>());
  const rawQueryParams = qs.parse(props.queryOverride || window.location.search, {
    ignoreQueryPrefix: true
  }) as Record<string, unknown>;
  const config = parseHealthOverAttemptConfig(rawQueryParams);
  const [validPulls, setValidPulls] = useState([]);
  const type = config.type;
  const widgetType = widgetTypes.find((w) => w.slug === type) || widgetTypes[0];
  const isTest = config.test;
  const requestedDifficulty = isTest ? TEST_PAYLOAD.raid.difficulty : config.difficulty;
  const hideData = config.hideData;
  const enablePhaseProgress = config.graphPhaseProgress;
  const showDottedLines = config.showDottedLines;
  const minPull = config.minPull;
  const maxPull = config.maxPull;
  const hasValidPullRange = !(minPull && maxPull && minPull > maxPull);
  const showDayMarkers = showDottedLines && !hideData.includes('day');

  const [currentShownPercentages, setCurrentShownPercentages] = useState<[number, number]>([0, 1]);
  const [currentShownPhaseIndexes, setCurrentShownPhaseIndexes] = useState<[number, number]>([0, 0]);
  const [phaseZoomInitialized, setPhaseZoomInitialized] = useState(false);
  const [phasesFilterInitialized, setPhasesFilterInitialized] = useState(false);
  const [phaseSegmentRangeInitialized, setPhaseSegmentRangeInitialized] = useState(false);

  const responseRef = useRef(null as any);
  const resolvedDifficulty =
    requestedDifficulty && requestedDifficulty !== 'latest'
      ? requestedDifficulty
      : responseRef.current?.raid?.difficulty || '';

  const phasesProgress = responseRef.current
    ? validPulls.map((p: any) =>
        getPhaseProgress(resolvedDifficulty, responseRef.current.boss.slug, {
          overallPercent: p.details.encounter_health.overall_percent,
          elapsedMs: p.details.duration_ms
        })
      )
    : [];

  const filteredPhasesProgress = phasesProgress.filter(Boolean);
  const areAllPhasesValid =
    filteredPhasesProgress.length === phasesProgress.length && filteredPhasesProgress.length > 0;

  const phasesSegments =
    areAllPhasesValid && responseRef.current.boss
      ? getPhaseSegments(resolvedDifficulty, responseRef.current.boss.slug)
      : [];
  const usesTimePhaseSegments = phasesSegments[0]?.metric === 'elapsedMs';
  const maxPhaseIndex = Math.max(phasesSegments.length - 1, 0);

  const segmentConfigurable = !!phasesSegments.length && enablePhaseProgress;

  useEffect(() => {
    if (usesTimePhaseSegments && phasesSegments.length > 0 && !phaseSegmentRangeInitialized) {
      setCurrentShownPhaseIndexes([0, maxPhaseIndex]);
      setPhaseSegmentRangeInitialized(true);
    }
  }, [maxPhaseIndex, phaseSegmentRangeInitialized, phasesSegments.length, usesTimePhaseSegments]);

  useEffect(() => {
    if (phasesSegments.length > 0 && !phaseZoomInitialized) {
      const phases = config.zoomPhases;
      if (phases.length > 0) {
        if (usesTimePhaseSegments) {
          let minIndex = maxPhaseIndex;
          let maxIndex = 0;
          let didMatchPhase = false;

          phasesSegments.forEach((segment, index) => {
            if (phases.includes(segment.phase.toString())) {
              minIndex = Math.min(minIndex, index);
              maxIndex = Math.max(maxIndex, index);
              didMatchPhase = true;
            }
          });

          if (
            didMatchPhase &&
            (minIndex !== currentShownPhaseIndexes[0] || maxIndex !== currentShownPhaseIndexes[1])
          ) {
            setCurrentShownPhaseIndexes([minIndex, maxIndex]);
          }
        } else {
          let min = 1;
          let max = 0;
          for (const segment of phasesSegments) {
            if (phases.includes(segment.phase.toString())) {
              min = Math.min(min, segment.minValue);
              max = Math.max(max, typeof segment.maxValue === 'number' ? segment.maxValue : 0);
            }
          }

          if (min !== currentShownPercentages[0] || max !== currentShownPercentages[1]) {
            setCurrentShownPercentages([min, max]);
          }
        }

        setPhaseZoomInitialized(true);
      }
    }
  }, [
    config.zoomPhasesParam,
    currentShownPercentages,
    currentShownPhaseIndexes,
    maxPhaseIndex,
    phaseZoomInitialized,
    phasesSegments,
    usesTimePhaseSegments
  ]);

  useEffect(() => {
    if (phasesSegments.length > 0 && !phasesFilterInitialized && config.phases.length >= 1) {
      const [startPhase, endPhase] = config.phases;

      const startIndex = phasesSegments.findIndex((segment) => segment.phase.toString() === startPhase);
      const endIndex = phasesSegments.findIndex((segment) => segment.phase.toString() === endPhase);

      if (usesTimePhaseSegments) {
        const startPhaseIndex = startIndex >= 0 ? startIndex : 0;
        const endPhaseIndex = endIndex >= 0 ? endIndex : startPhaseIndex;
        setCurrentShownPhaseIndexes([Math.min(startPhaseIndex, endPhaseIndex), Math.max(startPhaseIndex, endPhaseIndex)]);
      } else {
        const startSeg = startIndex >= 0 ? phasesSegments[startIndex] : null;
        const endSeg = endIndex >= 0 ? phasesSegments[endIndex] : null;

        // endPhase is exclusive: the threshold is where endPhase begins (= endSeg.maxValue)
        const minPercent = endSeg && typeof endSeg.maxValue === 'number' ? endSeg.maxValue : 0;
        const maxPercent = startSeg && typeof startSeg.maxValue === 'number' ? startSeg.maxValue : 1;

        setCurrentShownPercentages([minPercent, maxPercent]);
      }

      setPhasesFilterInitialized(true);
    }
  }, [config.phasesParam, maxPhaseIndex, phasesFilterInitialized, phasesSegments, usesTimePhaseSegments]);

  useEffect(() => {
    const refreshMs = config.refreshMs;
    let firstDisplayed = false;

    async function refreshData() {
      try {
        const guildList = isTest ? [null] : config.guilds;
        const validGuilds = _.compact<any>(
          guildList.map((guildInfo: any) => {
            if (guildInfo || config.test) {
              if (isTest) {
                const testRegion = TEST_PAYLOAD.guild.region;
                const testRealm = TEST_PAYLOAD.guild.realm;
                const testGuild = TEST_PAYLOAD.guild;
                responseRef.current = TEST_PAYLOAD;
                return { testRegion, testRealm, testGuild };
              }
              return guildInfo;
            }
            return null;
          })
        );

        if (validGuilds.length === 0) {
          throw new Error('Missing guilds params');
        }

        const guildsData: any = {
          guilds: [],
          boss: {},
          raid: {},
          series: [],
          days: [],
          widgetVersion: undefined
        };

        const animationDelay = config.delay;
        const seriesDays: any = [];
        const guildResponses: any[] = [];

        const animationDuration = firstDisplayed ? 2000 : 0;
        firstDisplayed = true;

        for (const guildInfo of validGuilds) {
          if (!isTest) {
            responseRef.current = await apiServiceAsync.viewLiveTrackingBossPulls({
              ...(config.raid ? { raid: config.raid } : {}),
              ...(config.difficulty ? { difficulty: config.difficulty } : {}),
              ...(config.boss ? { boss: config.boss } : {}),
              ...(config.accessKey ? { access_key: config.accessKey } : {}),
              ...(config.period ? { period: config.period } : {}),
              ...(guildsData.boss?.slug ? { boss: guildsData.boss.slug } : {}),
              ...(guildsData.raid?.slug ? { raid: guildsData.raid.slug } : {}),
              ...(config.skipAfter ? { max_date: config.skipAfter } : {}),
              ...(config.skipBefore ? { min_date: config.skipBefore } : {}),
              ...guildInfo
            });
          }

          guildResponses.push(responseRef.current);
        }

        if (!guildResponses.length) {
          throw new Error('No guild data returned');
        }

        const comparisonSeriesStyles = buildComparisonSeriesStyles(
          guildResponses.map((guildResponse) => guildResponse.guild),
          config.seriesColors
        );

        for (const [index, guildResponse] of guildResponses.entries()) {
          responseRef.current = guildResponse;
          guildsData.widgetVersion = guildResponse.widgetVersion;
          const guildDifficulty =
            requestedDifficulty && requestedDifficulty !== 'latest'
              ? requestedDifficulty
              : guildResponse.raid?.difficulty || '';

          const guildId = guildResponse.guild.id;

          if (!graphOption.current.has(guildId)) {
            graphOption.current.set(guildId, true);
          }

          const seriesStyle = comparisonSeriesStyles[index];
          const color = seriesStyle.color;

          const guildSeriesData = [];
          const guildBestSeriesData = [];

          const days: any = [];

          let attemptNumber = 1;

          let filteredPulls = guildResponse.pulls
            .filter((p: any) => !p.details.is_reset)
            .map((p: any) => {
              p.attemptNumber = attemptNumber++;

              let overallPercent = p.details.encounter_health.overall_percent * 100;
              if (
                guildResponse.boss.slug === 'sylvanas-windrunner' ||
                guildResponse.boss.slug === 'fated-sylvanas-windrunner'
              ) {
                let clampPercent: number;

                if (guildDifficulty === 'mythic') {
                  clampPercent = 45;
                } else {
                  clampPercent = 50;
                }

                // clamp all points to not be lower than yAxisMin
                if (overallPercent < clampPercent) {
                  overallPercent = clampPercent;
                }
              }

              p.overallPercent = overallPercent;

              return p;
            });

          let skippedPulls: any[] = [];
          if (config.skipFirst > 0) {
            skippedPulls = filteredPulls.splice(0, config.skipFirst);
          } else if (config.skipBefore) {
            const skipBefore = moment.utc(config.skipBefore);

            const rawFirstValidAttemptIndex = filteredPulls.findIndex((p: any) => {
              const pullStartedAt = moment.utc(p.details.pull_started_at);

              return pullStartedAt.isAfter(skipBefore);
            });
            const firstValidAttemptIndex =
              rawFirstValidAttemptIndex === -1 ? filteredPulls.length : rawFirstValidAttemptIndex;

            skippedPulls = filteredPulls.splice(0, firstValidAttemptIndex);
          }

          if (config.skipAfter) {
            const skipAfter = moment.utc(config.skipAfter);

            filteredPulls = filteredPulls.filter((p: any) => {
              const pullStartedAt = moment.utc(p.details.pull_started_at);

              return pullStartedAt.isBefore(skipAfter);
            });
          }

          if (hasValidPullRange && (minPull || maxPull)) {
            filteredPulls = filteredPulls.filter((pull: any) => {
              if (minPull && pull.attemptNumber < minPull) {
                return false;
              }

              if (maxPull && pull.attemptNumber > maxPull) {
                return false;
              }

              return true;
            });
          }

          const bestSkippedPull = skippedPulls.length > 0 ? _.minBy(skippedPulls, 'overallPercent') : null;
          let min = bestSkippedPull ? bestSkippedPull.overallPercent : 101;
          let bestEncounterDurationMs = bestSkippedPull?.details?.duration_ms || null;

          let previousData = null;
          setValidPulls(filteredPulls);

          for (const attempt of filteredPulls) {
            const newValue = widgetType.value(attempt, previousData);
            previousData = newValue;

            const pullStartedAt = moment.utc(attempt.details.pull_started_at);
            const currentDay = days.find((d: any) => pullStartedAt.diff(d.end, 'hours') <= 6);

            if (currentDay) {
              currentDay.end = pullStartedAt;
            } else {
              if (showDayMarkers && !config.hideDays) {
                days.push({
                  day: days.length + 1,
                  start: pullStartedAt,
                  end: pullStartedAt,
                  color, // Color value
                  guildId,
                  opacity: 0.1,
                  dashStyle: seriesStyle.dayDashStyle,
                  value: newValue,
                  width: graphOption.current.get(guildId) ? 1 : 0,
                  label: {
                    test: t('widgets:boss-health-progression.widget.day-count', { count: days.length + 1 }),
                    style: {
                      color,
                      display: graphOption.current.get(guildId) ? 'block' : 'none'
                    }
                  }
                });
              }
            }

            guildSeriesData.push({
              x: newValue,
              y: attempt.overallPercent,
              custom: {
                durationMs: attempt.details.duration_ms
              },
              marker: {
                enabled: false,
                symbol: seriesStyle.markerSymbol
              }
            });

            if (attempt.overallPercent < min) {
              min = attempt.overallPercent;
              bestEncounterDurationMs = attempt.details.duration_ms;
              guildBestSeriesData.push({
                x: newValue,
                y: min,
                custom: {
                  durationMs: attempt.details.duration_ms
                },
                marker: {
                  enabled: true,
                  symbol: seriesStyle.markerSymbol
                },
                dataLabels: {
                  enabled: true,
                  verticalAlign: 'top',
                  style: {
                    textOutline: false
                  },
                  color,
                  formatter: function fn() {
                    const phaseProgress = getPhaseProgress(guildDifficulty, responseRef.current.boss.slug, {
                      overallPercent: this.y / 100,
                      elapsedMs: this.point?.options?.custom?.durationMs
                    });

                    if (!phaseProgress) {
                      return `${this.y.toFixed(1)}%`;
                    }

                    return `${formatPhaseDisplay(phaseProgress, (bossPercent) => `${roundScore(bossPercent * 100, 1)}%`)}<br/>`;
                  }
                }
              });
            } else {
              guildBestSeriesData.push({
                x: newValue,
                y: min,
                custom: {
                  durationMs: bestEncounterDurationMs
                },
                marker: {
                  enabled: false,
                  symbol: seriesStyle.markerSymbol
                }
              });
            }
          }

          if (widgetType.addCustomData) {
            widgetType.addCustomData(guildSeriesData, guildBestSeriesData);
          }

          seriesDays.push(...days);

          if (!hideData.includes('attempt')) {
            guildsData.series.push({
              name: responseRef.current.guild.displayName,
              custom: {
                type: 'attempt',
                guildId
              },
              data: guildSeriesData,
              dashStyle: showDottedLines ? seriesStyle.dashStyle : 'Solid',
              color,
              visible: graphOption.current.get(guildId),
              opacity: hideData.includes('best') ? 1 : 0.3,
              connectNulls: false,
              animation: {
                defer: animationDelay,
                duration: animationDuration
              },
              events: {
                legendItemClick: (event: any) => {
                  const visible = event.target.visible;
                  const toHide = visible;
                  const chart = event.target.chart;
                  const bestSeries = chart.series.find(
                    (s: any) => s.options?.custom?.type === 'best' && s.options?.custom?.guildId === guildId
                  );

                  if (toHide) {
                    bestSeries?.hide();
                  } else {
                    bestSeries?.show();
                  }

                  if (showDayMarkers) {
                    seriesDays.forEach((l: any) => {
                      if (l.guildId === guildId) {
                        l.width = toHide ? 0 : 1;
                        l.label.style.display = toHide ? 'none' : 'block';
                      }
                    });

                    chart.xAxis[0].update({
                      plotLines: seriesDays.filter(widgetType.plotLinesFilter)
                    });
                  }

                  graphOption.current.set(guildId, !visible);
                }
              }
            });
          }

          if (!hideData.includes('best')) {
            guildsData.series.push({
              name: hideData.includes('attempt')
                ? responseRef.current.guild.displayName
                : `${t('widgets:boss-health-progression.widget.widget-tooltip.best-guild-pull', {
                    name: responseRef.current.guild.displayName
                  })}`,
              custom: {
                type: 'best',
                guildId
              },
              data: guildBestSeriesData,
              visible: graphOption.current.get(guildId),
              color,
              opacity: 1,
              connectNulls: false,
              showInLegend: hideData.includes('attempt'), // if attempt is shown, then we only show one
              events: {
                legendItemClick: (event: any) => {
                  const visible = event.target.visible;
                  const toHide = visible;
                  const chart = event.target.chart;
                  const bestSeries = chart.series.find(
                    (s: any) => s.options?.custom?.type === 'attempt' && s.options?.custom?.guildId === guildId
                  );

                  if (toHide) {
                    bestSeries?.hide();
                  } else {
                    bestSeries?.show();
                  }

                  if (showDayMarkers) {
                    seriesDays.forEach((l: any) => {
                      if (l.guildId === guildId) {
                        l.width = toHide ? 0 : 1;
                        l.label.style.display = toHide ? 'none' : 'block';
                      }
                    });

                    chart.xAxis[0].update({
                      plotLines: seriesDays.filter(widgetType.plotLinesFilter)
                    });
                  }

                  graphOption.current.set(guildId, !visible);
                }
              },
              animation: {
                defer: animationDelay,
                duration: animationDuration
              }
            });
          }

          guildsData.boss = guildResponse.boss;
          guildsData.raid = guildResponse.raid;
          guildsData.guilds.push({ ...guildResponse.guild, guildPrivacy: guildResponse.guildPrivacy });
        }

        if (showDayMarkers) {
          guildsData.days = seriesDays;
        }

        responseRef.current = guildResponses[guildResponses.length - 1];
        setData(guildsData);
      } catch (err) {
        console.error({ err }, 'Error caught retrieving boss progress');
      }

      if (refreshMs > 0) {
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
        }
        timeoutHandle = setTimeout(refreshData, refreshMs);
      }
    }

    refreshData();

    return () => {
      clearTimeout(timeoutHandle);
    };
  }, []);

  if (!data) {
    return null;
  }

  function drawTicksWithConfig() {
    return drawTicks.call(this, config, data);
  }

  const yAxisMin = 0;
  // let yAxisMin = undefined;
  let yAxisTickPositions = undefined;

  // if (data.boss.slug === 'sylvanas-windrunner' || data.boss.slug === 'fated-sylvanas-windrunner') {
  //   if (difficulty === 'mythic') {
  //     yAxisTickPositions = [45, 55, 70, 85, 100];
  //     yAxisMin = 45;
  //   } else {
  //     yAxisTickPositions = [50, 60, 70, 80, 90, 100];
  //     yAxisMin = 50;
  //   }
  // }

  const customBackground = buildHealthOverAttemptBackgroundColor(config.chromaRgb);

  const difficulties = new Map([
    ['normal', 'common:difficulty.normal.name'],
    ['heroic', 'common:difficulty.heroic.name'],
    ['mythic', 'common:difficulty.mythic.name'],
    ['latest', 'widgets:boss-health-progression.widget.latest']
  ]);
  const rawDifficulty =
    typeof resolvedDifficulty === 'string' && resolvedDifficulty.trim()
      ? resolvedDifficulty.trim()
      : data.raid?.difficulty;
  const normalizedDifficulty = typeof rawDifficulty === 'string' ? rawDifficulty.toLowerCase() : undefined;
  const difficultyLabel = getTranslatedHealthOverAttemptLabel(
    normalizedDifficulty ? difficulties.get(normalizedDifficulty) : undefined,
    normalizedDifficulty ? _.startCase(normalizedDifficulty) : undefined
  );
  const widgetTypeSubtitle = getTranslatedHealthOverAttemptLabel(widgetType.subtitleKey);
  const raidLabel = getTranslatedHealthOverAttemptLabel(
    data.raid?.slug ? `raids:${data.raid.slug}.name` : undefined,
    data.raid?.name
  );
  const subtitleText =
    props.subtitle ||
    buildHealthOverAttemptSubtitle({
      widgetTypeSubtitle,
      guilds: data.guilds,
      guildsQueryParam: config.guildsQueryParam,
      difficultyLabel,
      raidLabel
    });

  let filteredPhasesSegments = phasesSegments;
  let seriesData = data.series;

  if (segmentConfigurable) {
    if (usesTimePhaseSegments) {
      filteredPhasesSegments = phasesSegments.filter((_, index) => {
        return index >= currentShownPhaseIndexes[0] && index <= currentShownPhaseIndexes[1];
      });

      seriesData = filterSeriesDataByPhaseSegmentIndexes(
        seriesData,
        resolvedDifficulty,
        data.boss.slug,
        phasesSegments,
        currentShownPhaseIndexes
      );
    } else {
      filteredPhasesSegments = phasesSegments.filter((segment) => {
        return (
          segment.minValue < currentShownPercentages[1] &&
          (typeof segment.maxValue !== 'number' || segment.maxValue >= currentShownPercentages[0])
        );
      });

      seriesData = seriesData.map((series: any) => {
        const filteredData = series.data.filter((point: any) => {
          return point.y >= currentShownPercentages[0] * 100 && point.y <= currentShownPercentages[1] * 100;
        });

        return {
          ...series,
          data: filteredData
        };
      });
    }
  }

  const allSeriesEmpty = seriesData.every((series: any) => series.data.length === 0);
  const chartHeight = props.height || config.height || 500;

  const getCreditsText = () => {
    const textParts = [];

    // NOTE: must access via data.series to get the original pull count
    for (let i = 0; i < data.series.length; ++i) {
      const originalSeries = data.series[i];
      const filteredSeries = seriesData[i];

      const percent = ((100 * filteredSeries.data.length) / originalSeries.data.length).toFixed(2);
      if (filteredSeries.data.length !== originalSeries.data.length) {
        textParts.push(
          `${originalSeries.name} ${filteredSeries.data.length} of ${originalSeries.data.length} ${plural('pull', originalSeries.data.length)} (${percent}%)`
        );
      } else {
        textParts.push(
          `${originalSeries.name} ${originalSeries.data.length} ${plural('pull', originalSeries.data.length)}`
        );
      }
    }

    if (!config.hidePoweredBy) {
      textParts.push(
        `${(typeof widgetType.credits === 'function' ? widgetType.credits() : widgetType.credits) || ''}${t(
          'widgets:boss-health-progression.widget.widget-credits-powered-by'
        )} Raider.IO - ${RIO_SITE.external_url}/widgets`
      );
    }

    return textParts.join(' • ');
  };

  const chartProps: any = {
    container: props.id || 'healthoverattempt-container',
    options: {
      chart: {
        ...(config.theme
          ? {
              style: {
                fontFamily: 'inherit'
              }
            }
          : {}),
        events: {
          load: drawTicksWithConfig,
          redraw: drawTicksWithConfig
        },
        ...(props.width ? { width: props.width } : {}),
        height: chartHeight,
        type: 'line',
        spacing: [18, 10, 16, 10],
        borderColor: '#151314',
        borderWidth: 2,
        backgroundColor: customBackground || {
          linearGradient: {
            x1: 0,
            x2: 0,
            y1: 0,
            y2: 1
          },
          stops: [
            [0, '#342f2d'],
            [1, '#151314']
          ]
        }
      },
      colors: ['#2f7ed8', '#8bbc21', '#910000', '#1aadce', '#492970', '#f28f43', '#77a1e5', '#c42525', '#a6c96a'],
      plotOptions: {},
      legend: {
        enabled: true,
        itemDistance: 60,
        itemStyle: {
          color: '#fff'
        },
        itemMarginTop: 10,
        itemMarginBottom: 10
      },
      series: seriesData,
      ...(props.hideSubtitle
        ? {
            subtitle: {
              text: null
            }
          }
        : {
            subtitle: {
              text: subtitleText,
              align: 'center',
              style: {
                color: '#ccc',
                fontSize: '1rem'
              }
            }
          }),
      ...(props.hideTitle
        ? {
            title: {
              text: null
            }
          }
        : {
            title: {
              text: t(`raids:${data.raid?.slug}.bosses.${data.boss.slug}.name`),
              align: 'center',
              style: {
                marginLeft: '0px',
                paddingLeft: '0px',
                color: '#fff',
                fontSize: '2rem'
              }
            }
          }),
      xAxis: {
        type: widgetType.xAxisType,
        visible: true,
        color: '#fff',
        lineColor: '#bbb',
        lineWidth: 1,
        tickColor: 'transparent',
        tickWidth: 2,
        tickLength: 30,
        plotLines: data.days.filter(widgetType.plotLinesFilter),
        labels: {
          formatter: widgetType.formatter
        }
      },
      yAxis: {
        visible: true,
        gridLineColor: 'rgba(255, 255, 255, 0.1)',

        title: {
          enabled: false
        },
        ...(segmentConfigurable && !usesTimePhaseSegments
          ? {
              ...buildPercentPhaseAxisConfig(filteredPhasesSegments, currentShownPercentages),
              labels: {
                formatter: function fn(this: any) {
                  const segment = filteredPhasesSegments.find(
                    (candidate) =>
                      typeof candidate.maxValue === 'number' && Math.abs(candidate.maxValue * 100 - this.value) === 0
                  );
                  return segment ? segment.phaseLabel : `${this.value}`;
                }
              }
            }
          : {
              min: typeof props.yAxisMin === 'number' ? props.yAxisMin : yAxisMin,
              max: 100,
              tickPositions: yAxisTickPositions,
              labels: {
                formatter(chart: any) {
                  return `${chart.value}%`;
                }
              }
            })
      },
      exporting: {
        enabled: false
      },
      credits: {
        enabled: !config.hideCredits,
        href: RIO_SITE.external_url,
        text: getCreditsText()
      },
      tooltip: {
        // enabled: true,
        // headerFormat: '<b>{point.x}</b><br/>',
        // pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
        animation: false,
        shape: 'square',
        headerFormat: '',
        footerFormat: '',
        pointFormatter: generatePullTooltip(widgetType, _.lowerCase(resolvedDifficulty), data.boss.slug),
        // positioner(width: any, height: any, plot: any) {
        //   return { x: plot.plotX - width / 2, y: plot.plotY + 5 };
        // },
        backgroundColor: null,
        borderWidth: 0,
        hideDelay: 200,
        useHTML: true
      },
      annotations: props.showPullAnnotations
        ? [
            {
              draggable: '',
              labelOptions: {
                x: -40,
                y: -10
              },
              labels: [
                ...(data.series.length > 0 && data.series[0].data.length > 0
                  ? [
                      {
                        point: {
                          xAxis: 0,
                          yAxis: 0,
                          x: data.series[0].data.length - 1,
                          y: data.series[0].data[data.series[0].data.length - 1].y,
                          align: 'top'
                        },
                        text: `&lt;${data.series[0].name}&gt;<br />${t(
                          'widgets:boss-health-progression.widget.latest-pull'
                        )}`,
                        y: -50
                      }
                    ]
                  : []),
                ...(data.series.length > 2 && data.series[2].data.length > 0
                  ? [
                      {
                        point: {
                          xAxis: 0,
                          yAxis: 0,
                          x: data.series[2].data.length - 1,
                          y: data.series[2].data[data.series[2].data.length - 1].y,
                          align: 'top'
                        },
                        text: `&lt;${data.series[2].name}&gt;<br />${t(
                          'widgets:boss-health-progression.widget.latest-pull'
                        )}`,
                        y: -50
                      }
                    ]
                  : [])
              ]
            }
          ]
        : []
    }
  };

  const darkModeGuilds = data.guilds.filter((guild: any) => {
    const shareRaidUntil = guild.guildPrivacy?.shareRaidUntil;
    return shareRaidUntil ? moment.utc(shareRaidUntil).isBefore(moment.utc()) : false;
  });

  let emptyStateMessage: string | null = null;
  if (allSeriesEmpty) {
    emptyStateMessage = t('widgets:boss-health-progression.widget.no-pulls-match-filters');

    if (hasValidPullRange && minPull && maxPull) {
      emptyStateMessage = t('widgets:boss-health-progression.widget.no-pulls-in-range', { minPull, maxPull });
    } else if (hasValidPullRange && minPull) {
      emptyStateMessage = t('widgets:boss-health-progression.widget.no-pulls-from-min-pull', { minPull });
    } else if (hasValidPullRange && maxPull) {
      emptyStateMessage = t('widgets:boss-health-progression.widget.no-pulls-up-to-max-pull', { maxPull });
    }
  }

  return (
    <div style={{ position: 'relative' }} className={config.theme ? `widget-theme-${config.theme}` : ''}>
      {darkModeGuilds.length > 0 && (
        <ComplexTooltip
          renderJustChildren
          tooltip={
            <Stack direction="vertical" divider={<hr style={{ margin: '4px 0' }} />}>
              <div style={{ maxWidth: 280 }}>
                <Typography.Title size="small" strong>
                  <Trans i18nKey="guilds:dark-mode.guild-in-dark-mode">
                    <i className="dark-mode-icon" />
                  </Trans>
                </Typography.Title>
                <ul>
                  {darkModeGuilds.map((guild: any) => {
                    const shareRaidUntil = guild.guildPrivacy?.shareRaidUntil;
                    return (
                      <li>
                        <Typography.Text size="medium">
                          <Trans
                            i18nKey="guilds:dark-mode.guild-dark-for"
                            values={{ name: guild.name, duration: relativeDate(shareRaidUntil) }}
                          >
                            <i />
                          </Trans>
                        </Typography.Text>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </Stack>
          }
        >
          <i style={{ position: 'absolute', right: 8, top: 8, zIndex: 1000 }} className="dark-mode-icon" />
        </ComplexTooltip>
      )}

      {segmentConfigurable && (
        <div style={{ position: 'absolute', left: 16, bottom: 4, zIndex: 1000, width: 220 }}>
          {usesTimePhaseSegments ? (
            <FormControlSlider
              min={0}
              max={maxPhaseIndex}
              step={1}
              range
              value={currentShownPhaseIndexes}
              onChange={(value) => {
                if (Array.isArray(value)) {
                  const newValue = value as [number, number];
                  setCurrentShownPhaseIndexes(newValue);

                  const [startIndex, endIndex] = newValue;
                  let phasesValue: string | null = null;
                  if (!(startIndex === 0 && endIndex === maxPhaseIndex)) {
                    const startSeg = phasesSegments[startIndex];
                    const endSeg = phasesSegments[endIndex];
                    if (startSeg && endSeg) {
                      phasesValue =
                        startSeg.phase === endSeg.phase
                          ? `${startSeg.phase}`
                          : `${startSeg.phase},${endSeg.phase}`;
                    } else if (startSeg) {
                      phasesValue = `${startSeg.phase}`;
                    }
                  }
                  props.onPhasesChange?.(phasesValue);
                }
              }}
              marks={Object.fromEntries(phasesSegments.map((segment, index) => [index, segment.phaseLabel]))}
            />
          ) : (
            <FormControlSlider
              min={0}
              max={1}
              step={null}
              range
              reverse
              value={currentShownPercentages}
              onChange={(value) => {
                if (Array.isArray(value)) {
                  const newValue = value as [number, number];
                  setCurrentShownPercentages(newValue);

                  const [minPercent, maxPercent] = newValue;
                  const startSeg = phasesSegments.find((segment) => segment.maxValue === maxPercent);
                  const endSeg = phasesSegments.find((segment) => segment.maxValue === minPercent);
                  let phasesValue: string | null = null;
                  if (!(maxPercent >= 1 && minPercent <= 0)) {
                    if (startSeg && endSeg) {
                      phasesValue = `${startSeg.phase},${endSeg.phase}`;
                    } else if (startSeg) {
                      phasesValue = `${startSeg.phase}`;
                    }
                  }
                  props.onPhasesChange?.(phasesValue);
                }
              }}
              marks={{
                0: '0%',
                ...Object.fromEntries(
                  phasesSegments
                    .filter((segment) => typeof segment.maxValue === 'number')
                    .map((segment) => [segment.maxValue as number, segment.phaseLabel])
                )
              }}
            />
          )}
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <Chart className="rio-background--header" {...props.ui} {...chartProps} />
        {emptyStateMessage && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#d8d8d8',
              textAlign: 'center',
              pointerEvents: 'none'
            }}
          >
            {emptyStateMessage}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * This method returns an object with static data. In the future we may want to update this to accept params such as Guild, Realm, Boss, etc.
 * @returns Returns an object mocking what a response may look like from the API to populate the Health Over Attempt widget in test mode.
 */
function getTestPayload() {
  return {
    guild: {
      name: 'Melee Mechanics',
      realm: {
        name: 'Illidan',
        slug: 'illidan'
      },
      region: {
        slug: 'us',
        short_name: 'US'
      }
    },
    raid: {
      difficulty: 'mythic',
      slug: 'fated-castle-nathria'
    },
    boss: {
      name: 'Fated Sire Denathrius',
      slug: 'fated-sire-denathrius'
    },
    pulls: [
      {
        details: {
          is_reset: false,
          duration_ms: 69631,
          pull_started_at: '2022-08-09T01:35:08.131Z',
          pull_ended_at: '2022-08-09T01:36:17.762Z',
          encounter_health: {
            overall_percent: 0.873656,
            boss_percent: 0.873656,
            phase: null,
            progress_display: '87.37%'
          }
        }
      },
      {
        details: {
          is_reset: false,
          duration_ms: 583685,
          pull_started_at: '2022-08-26T01:19:36.855Z',
          pull_ended_at: '2022-08-26T01:29:20.461Z',
          encounter_health: {
            overall_percent: 0.0389255,
            boss_percent: 0.0389255,
            phase: null,
            progress_display: '3.89%'
          }
        }
      },
      {
        details: {
          is_reset: false,
          duration_ms: 287383,
          pull_started_at: '2022-08-26T01:32:31.929Z',
          pull_ended_at: '2022-08-26T01:37:19.312Z',
          encounter_health: {
            overall_percent: 0.601894,
            boss_percent: 0.601894,
            phase: null,
            progress_display: '60.19%'
          }
        }
      },
      {
        details: {
          is_reset: false,
          duration_ms: 444918,
          pull_started_at: '2022-08-26T01:39:11.090Z',
          pull_ended_at: '2022-08-26T01:46:36.053Z',
          encounter_health: {
            overall_percent: 0.315501,
            boss_percent: 0.315501,
            phase: null,
            progress_display: '31.55%'
          }
        }
      },
      {
        details: {
          is_reset: false,
          duration_ms: 465332,
          pull_started_at: '2022-08-26T01:57:16.831Z',
          pull_ended_at: '2022-08-26T02:05:02.243Z',
          encounter_health: {
            overall_percent: 0.30545,
            boss_percent: 0.30545,
            phase: null,
            progress_display: '30.55%'
          }
        }
      },
      {
        details: {
          is_reset: false,
          duration_ms: 571134,
          pull_started_at: '2022-08-26T02:21:56.586Z',
          pull_ended_at: '2022-08-26T02:31:27.720Z',
          encounter_health: {
            overall_percent: 0,
            boss_percent: 0,
            phase: null,
            progress_display: '0%'
          }
        }
      }
    ]
  };
}

export default withSiteMeta(WidgetHealthOverAttempt);
